// Turns a runner transcript (Claude Code stream-json, one event per line, each stamped with the
// runner's `ts`) into the steps the timeline shows: what the model said, which tools it called
// with what, what came back, and how the run ended.

export type TToolResult = { text: string; isError: boolean; ts: string | null };

export type TTimelineItem =
  | { kind: "init"; ts: string | null; model: string | null; sessionId: string | null; tools: string[] }
  | { kind: "text"; ts: string | null; text: string; subagent: boolean }
  | { kind: "thinking"; ts: string | null; text: string; subagent: boolean }
  | {
      kind: "tool";
      ts: string | null;
      id: string;
      name: string;
      input: unknown;
      summary: string;
      result: TToolResult | null;
      subagent: boolean;
    }
  | {
      kind: "result";
      ts: string | null;
      subtype: string | null;
      isError: boolean;
      durationMs: number | null;
      numTurns: number | null;
      costUsd: number | null;
      usage: unknown;
      text: string | null;
    }
  | { kind: "other"; ts: string | null; type: string; raw: unknown };

export type TTranscript = {
  items: TTimelineItem[];
  unreadableLines: number;
  startedAt: string | null;
  endedAt: string | null;
};

type TRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is TRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const str = (value: unknown) => (typeof value === "string" ? value : null);
const num = (value: unknown) => (typeof value === "number" && Number.isFinite(value) ? value : null);

const SUMMARY_KEYS = ["file_path", "command", "pattern", "query", "url", "description", "prompt", "notebook_path"];
const SUMMARY_MAX = 140;

export const summarizeToolInput = (input: unknown): string => {
  if (!isRecord(input)) return "";
  for (const key of SUMMARY_KEYS) {
    const value = input[key];
    if (typeof value === "string" && value.trim()) {
      const oneLine = value.trim().split("\n")[0];
      return oneLine.length > SUMMARY_MAX ? `${oneLine.slice(0, SUMMARY_MAX)}…` : oneLine;
    }
  }
  return "";
};

const resultText = (content: unknown): string => {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((block) => (isRecord(block) && block.type === "text" && typeof block.text === "string" ? block.text : ""))
    .filter(Boolean)
    .join("\n");
};

const contentBlocks = (event: TRecord): TRecord[] => {
  const message = isRecord(event.message) ? event.message : null;
  const content = message?.content;
  return Array.isArray(content) ? content.filter(isRecord) : [];
};

export const parseTranscript = (text: string): TTranscript => {
  const items: TTimelineItem[] = [];
  const toolsById = new Map<string, Extract<TTimelineItem, { kind: "tool" }>>();
  let unreadableLines = 0;
  let startedAt: string | null = null;
  let endedAt: string | null = null;

  for (const line of text.split("\n")) {
    if (!line.trim()) continue;
    let event: unknown;
    try {
      event = JSON.parse(line);
    } catch {
      unreadableLines += 1;
      continue;
    }
    if (!isRecord(event) || typeof event.type !== "string") {
      unreadableLines += 1;
      continue;
    }
    // Runner transcripts carry the runner's `ts`; Claude Code's own session files carry `timestamp`.
    const ts = str(event.ts) ?? str(event.timestamp);
    if (ts) {
      startedAt ??= ts;
      endedAt = ts;
    }
    const subagent = typeof event.parent_tool_use_id === "string";

    if (event.type === "system" && event.subtype === "init") {
      items.push({
        kind: "init",
        ts,
        model: str(event.model),
        sessionId: str(event.session_id),
        tools: Array.isArray(event.tools) ? event.tools.filter((t): t is string => typeof t === "string") : []
      });
      continue;
    }

    if (event.type === "assistant") {
      for (const block of contentBlocks(event)) {
        if (block.type === "text" && typeof block.text === "string" && block.text.trim()) {
          items.push({ kind: "text", ts, text: block.text, subagent });
        } else if (block.type === "thinking" && typeof block.thinking === "string" && block.thinking.trim()) {
          items.push({ kind: "thinking", ts, text: block.thinking, subagent });
        } else if (block.type === "tool_use" && typeof block.id === "string") {
          const item: Extract<TTimelineItem, { kind: "tool" }> = {
            kind: "tool",
            ts,
            id: block.id,
            name: str(block.name) ?? "tool",
            input: block.input,
            summary: summarizeToolInput(block.input),
            result: null,
            subagent
          };
          toolsById.set(block.id, item);
          items.push(item);
        }
      }
      continue;
    }

    if (event.type === "user") {
      let matched = false;
      for (const block of contentBlocks(event)) {
        if (block.type !== "tool_result" || typeof block.tool_use_id !== "string") continue;
        const tool = toolsById.get(block.tool_use_id);
        if (!tool) continue;
        tool.result = { text: resultText(block.content), isError: block.is_error === true, ts };
        matched = true;
      }
      if (!matched) items.push({ kind: "other", ts, type: "user", raw: event });
      continue;
    }

    if (event.type === "result") {
      items.push({
        kind: "result",
        ts,
        subtype: str(event.subtype),
        isError: event.is_error === true,
        durationMs: num(event.duration_ms),
        numTurns: num(event.num_turns),
        costUsd: num(event.total_cost_usd),
        usage: event.usage,
        text: str(event.result)
      });
      continue;
    }

    items.push({ kind: "other", ts, type: event.type, raw: event });
  }

  return { items, unreadableLines, startedAt, endedAt };
};
